/**
 * Il campo allegato (punto 13).
 *
 * Carica sulla **stessa rotta del dialog del task** e con le stesse regole:
 * `attachmentVerdict` decide — formati, tetto degli allegati, spazio della
 * subscription, deduplica per nome — e qui non si ripete nessuna di quelle
 * regole. Il verdetto dice cosa fare, questo file lo esegue.
 *
 * Senza `host` il campo è in sola lettura **e lo dice**: il token di caricamento
 * appartiene alla superficie che ha il task, e dove non c'è (l'avvio di un
 * processo) un pulsante che non funziona sarebbe peggio di nessun pulsante.
 */

import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { AttachFile, Close } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { getTranslation } from "common";
import { SUPPORTED_EXTENSIONS, getFileExtension } from "common/utilities";
import { useSnackbar, useTranslations } from "hooks";
import AttachmentList from "components/AttachmentList";
import {
  attachmentNames,
  attachmentVerdict,
  withAttachmentIds,
  withoutAttachmentId,
  type AttachmentHost,
} from "./attachmentField";

interface AttachmentFieldInputProps {
  /** Assente dove non si può caricare: vedi in testa al file. */
  host?: AttachmentHost | null;
  /** Gli id degli allegati. */
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  label: string;
  testId: string;
  /**
   * Se il file si può rinominare da qui.
   *
   * È `config.allowRename` della definizione del campo: la matita la concede il
   * progettista, una volta per il campo. Assente significa no.
   */
  allowRename?: boolean;
}

const AttachmentFieldInput: React.FC<AttachmentFieldInputProps> = ({
  host,
  value,
  onChange,
  disabled = false,
  label,
  testId,
  allowRename = false,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const files = attachmentNames(value, host?.available ?? []);
  const readOnly = disabled || !host;
  // Le due parti della riga: i file che ci sono — con gli stessi comandi
  // dell'elenco allegati del task — e gli id rimasti senza file, che si
  // mostrano (con la X che toglie il riferimento) invece di sparire.
  const byId = new Map((host?.available ?? []).map((a) => [String(a.id), a]));
  const known = files.flatMap((file) => {
    const attachment = byId.get(file.id);
    return attachment ? [attachment] : [];
  });
  const missing = files.filter((file) => !byId.has(file.id));

  const handlePicked = async (picked: FileList | null) => {
    if (!host || !picked || picked.length === 0) return;

    const scelti = Array.from(picked);
    const supportati: File[] = [];
    for (const file of scelti) {
      const ext = getFileExtension(file.name);
      if (!ext || !SUPPORTED_EXTENSIONS.includes(ext.toLowerCase())) {
        // Stessa risposta del dialog del task, stessa chiave.
        sendSnack({
          message: getTranslation(translations, "attachment.extension.unsupported"),
          type: "error",
        });
        continue;
      }
      supportati.push(file);
    }
    if (supportati.length === 0) return;

    const verdetto = attachmentVerdict(
      supportati,
      host.available.map((item) => item.filename),
      host.budget
    );

    if (verdetto.issueKey) {
      sendSnack({
        message: getTranslation(translations, verdetto.issueKey),
        type: "error",
      });
      return;
    }

    for (const doppio of verdetto.duplicated) {
      sendSnack({
        message: `${getTranslation(translations, "attachment.already.present")}: ${doppio.name}`,
        type: "info",
      });
    }

    if (verdetto.accepted.length === 0) return;

    setUploading(true);
    try {
      const ids = await host.upload(verdetto.accepted);
      onChange(withAttachmentIds(value, ids));
      sendSnack({
        type: "success",
        message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
      });
    } catch {
      // L'errore di rete lo dice già l'intercettore: qui non si aggiunge una
      // seconda frase che direbbe la stessa cosa con parole diverse.
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap={0.5}>
      {/* La label sopra, come negli altri campi: un allegato senza file mostra
          comunque un elenco vuoto, e una label dentro quel vuoto non avrebbe un
          campo a cui appartenere. */}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      {files.length > 0 && (
        <Box data-testid={testId}>
          {/* I file che ci sono si mostrano con la stessa riga — e gli stessi
              comandi, storia compresa — dell'elenco allegati del task: un solo
              posto dove si fa anteprima, si rinomina, si scarica, si elimina un
              allegato e si consultano le sue versioni. */}
          {known.length > 0 && (
            <AttachmentList
              compact
              attachments={known}
              canManage={!readOnly}
              knownWidth={0}
              histories={host?.histories}
              allowRename={allowRename}
              getAttachments={host?.refresh}
              onDeleted={(attachment) => onChange(withoutAttachmentId(value, attachment.id))}
            />
          )}
          {missing.length > 0 && !host?.loading && (
            <List dense disablePadding>
              {missing.map((file) => (
                <ListItem
                  key={file.id}
                  disableGutters
                  secondaryAction={
                    readOnly ? undefined : (
                      <IconButton
                        size="small"
                        data-testid={`${testId}-remove-${file.id}`}
                        onClick={() => onChange(withoutAttachmentId(value, file.id))}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    // Un allegato cancellato si mostra, non si nasconde: il campo
                    // direbbe di avere un documento che non c'è più.
                    primary={getTranslation(translations, "custom_fields.attachment.missing")}
                    primaryTypographyProps={{ variant: "body2", color: "warning.main" }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {files.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {getTranslation(translations, "custom_fields.attachment.none")}
        </Typography>
      )}

      {readOnly ? (
        !disabled && (
          <Typography variant="caption" color="text.secondary">
            {getTranslation(translations, "custom_fields.attachment.upload_where")}
          </Typography>
        )
      ) : files.length > 0 ? null : (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFile fontSize="small" />}
            disabled={uploading}
            data-testid={`${testId}-pick`}
            onClick={() => inputRef.current?.click()}
            sx={{ borderRadius: 5, textTransform: "none" }}
          >
            {getTranslation(translations, "custom_fields.attachment.add")}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept={SUPPORTED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
            data-testid={`${testId}-input`}
            onChange={(e) => {
              void handlePicked(e.target.files);
              // Lo stesso file scelto due volte di fila deve poter ripartire:
              // senza azzerare, il secondo `change` non arriva.
              e.target.value = "";
            }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default AttachmentFieldInput;
